import { Alert, Autocomplete, Box, Chip, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import { StyledTextField } from "../../../../components/forms/styles/formFieldStyles";

const QualysConfigPage = () => {
  const queryClient = useQueryClient();
  const { fetchQualysTags, getQualysConfiguration, updateQualysConfiguration } =
    useAPI();

  const [pendingChanges, setPendingChanges] = useState(null);

  const {
    data: tagsData,
    isLoading: tagsLoading,
    error: tagsError,
  } = useQuery({
    queryKey: ["qualys", "tags"],
    queryFn: async () => {
      const response = await fetchQualysTags();
      return response.data.tags;
    },
  });

  const {
    data: configData,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ["qualys", "configuration"],
    queryFn: async () => {
      const response = await getQualysConfiguration();
      return response.data.selected_tag_ids;
    },
  });

  const updateConfigMutation = useMutation({
    mutationFn: updateQualysConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["qualys", "configuration"] });
      queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      Toast.success("Qualys configuration updated successfully");
      setPendingChanges(null);
    },
    onError: (error) => {
      if (error.response) {
        Toast.error(
          `Failed to update configuration: ${error.response.data.detail}`
        );
      } else {
        Toast.error("An error occurred while updating configuration");
      }
    },
  });

  const selectedTags = pendingChanges ?? configData ?? [];
  const hasChanges = pendingChanges !== null;

  const handleTagChange = (event, newValue) => {
    setPendingChanges(newValue.map((tag) => tag.id));
  };

  const handleSave = () => {
    if (pendingChanges !== null) {
      updateConfigMutation.mutate({ selected_tag_ids: pendingChanges });
    }
  };

  if (tagsLoading || configLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <LoadingIcon size={25} />
      </Box>
    );
  }

  if (tagsError || configError) {
    return (
      <Alert severity="error">
        Error loading Qualys configuration. Please try again later.
      </Alert>
    );
  }

  const tagOptions = tagsData || [];
  const selectedTagObjects = tagOptions.filter((tag) =>
    selectedTags.includes(tag.id)
  );

  return (
    <Box width={700}>
      <Typography variant="h4" mb={2}>
        Configure Qualys Integration
      </Typography>

      <Typography variant="body2" mb={2}>
        Select which tags to monitor. If no tags are selected, no assets will be
        monitored.
      </Typography>

      {selectedTags.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No tags selected - no assets will be monitored.
        </Alert>
      ) : (
        <>
          <Typography sx={{ mb: 2 }}>
            Currently monitoring assets with {selectedTags.length} tag
            {selectedTags.length !== 1 ? "s" : ""}
          </Typography>
        </>
      )}

      <Box mb={3}>
        <Autocomplete
          multiple
          options={tagOptions}
          value={selectedTagObjects}
          onChange={handleTagChange}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label={
                <Typography variant="caption">Select Qualys Tags</Typography>
              }
              placeholder={
                selectedTags.length === 0 ? "No tags selected" : "Add more tags"
              }
              margin="dense"
              fullWidth
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option.id}
                label={option.name}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </Box>

      <Box display="flex" justifyContent="flex-start" gap={2}>
        <PrimaryButton
          onClick={handleSave}
          disabled={!hasChanges || updateConfigMutation.isPending}
        >
          {updateConfigMutation.isPending ? "Saving..." : "Save Configuration"}
        </PrimaryButton>
        {hasChanges && (
          <Typography variant="body2" color="text.secondary" alignSelf="center">
            You have unsaved changes
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default QualysConfigPage;
